import React from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {Hall} from "types/entities/Hall";

interface SidePanelItemProps {
    svg: string;
    label: string;
    value: string;
    last?: boolean;
}
const SidePanelItem = (props: SidePanelItemProps) => {
    const {last} = props || false;
    return (
        <div className={"py-1 px-8 flex flex-row items-center select-none"}>
            <div className={"h-16 w-16 mr-8"}>
                <img className="w-full h-full object-contain" src={`/img/${props.svg}.svg`} />
            </div>
            <div className={"flex-grow flex flex-col justify-start text-left "}>
                <span className={"w-full text-lg font-bold"}>{props.label}</span>
                <span className={"text-red-300 text-lg"}>{props.value}</span>
                {!last && <hr className={"mt-2 w-20"} />}
            </div>
        </div>
    );
};

interface SidePanelProps {
    halls: Hall[];
    active: number | null;
    setActive: React.Dispatch<number>;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    onClick: (e) => void;
}
const SidePanel = (props: SidePanelProps) => {
    const {t} = useTranslation();
    const {halls, active, setActive, isOpen, setIsOpen} = props;

    const handleClick = (hallIndex, open) => {
        setActive(hallIndex);
        setIsOpen(open);
    };
    return (
        <div className={"SidePanel absolute inset-y-0 right-0 w-72 xl:w-112 bg-white p-4 shadow-md border-l-2 border-gray-300 space-y-8"}>
            {halls.map((hall, hallIndex) => {
                return (
                    <div key={hallIndex} className={"SidePanelHall py-2  flex flex-col"}>
                        <button role={"button"} onClick={() => handleClick(hallIndex, true)}>
                            <figure className={"SidePanelImage h-18 px-2 mx-auto flex flex-row justify-between items-center select-none"}>
                                <div className={"SidePanelStatus h-4 w-4 mr-2"}>
                                    <span
                                        className={clsx(hall.status === "opened" ? "bg-green-400" : "bg-red-500", "rounded-full w-full h-full flex flex-col")}
                                    ></span>
                                </div>
                                <div className={"SidePanelTrack flex flex-col flex-grow justify-center items-center"}>
                                    <div className={"text-2xl font-bold leading-4"}>{hall.name}</div>
                                    {/*<img alt={hall.name} src={"/img/" + hall.image} className={"w-full h-full object-contain"} />*/}
                                    <span className={"mt-2 text-center w-full"}>{hall.short_address}</span>
                                    <span className={"w-40 pt-4 border-b border-gray-300"}></span>
                                </div>
                            </figure>
                        </button>

                        <div
                            className={clsx(
                                "SidePanelSlideover",
                                active === hallIndex && isOpen ? "translate-x-0" : "translate-x-full",
                                " w-72 xl:w-112 absolute inset-y-0 right-0 bg-white transform transform-gpu duration-500 transition-all overflow-hidden"
                            )}
                        >
                            <div className={"p-4 text-center w-full h-full bg-white select-none flex flex-col justify-between"}>
                                <figure className={"SidePanelImage h-18 py-2 px-4 mx-auto flex flex-col justify-center items-center"}>
                                    <div className={"text-2xl font-medium relative inline-block p-2"}>
                                        <div
                                            className={clsx(hall.status === "opened" ? "bg-green-400" : "bg-red-600", "w-3 h-3 rounded-full inline-flex mr-2")}
                                        ></div>
                                        <span>{hall.name}</span>
                                    </div>
                                    {hall.track_map && <img alt={hall.name} className={"w-full h-full object-contain p-4"} src={"/img/" + hall.track_map} />}
                                    <div className={"grid grid-cols-2 gap-4 border-b border-gray-50 py-6"}>
                                        <div className={"flex flex-row justify-start items-center"}>
                                            <img className={"w-4 h-4 object-contain filter-black"} src={"/img/local.png"} />
                                            <div className={"ml-3 flex flex-col text-xs leading-3 text-left"}>{hall.address}</div>
                                        </div>

                                        <div className={"flex flex-row justify-start items-center"}>
                                            <img className={"w-4 h-4 object-contain filter-black"} src={"/img/info.png"} />
                                            <div className={"ml-3 flex flex-col text-xs leading-3 text-left"}>
                                                <span>{hall.description}</span>
                                                <span>{hall.karts?.length || 0} go-karts</span>
                                                <span>Since {hall.year}</span>
                                            </div>
                                        </div>
                                    </div>
                                </figure>
                                <div className={"SidePanelItems divide-dashed flex-grow py-2"}>
                                    <SidePanelItem key={hallIndex} label={"Karts on track"} svg={"kart"} value={"06 / 08"} />
                                    <SidePanelItem label={"Waiting time"} svg={"time"} value={"25 mins"} />
                                    <SidePanelItem label={"Number of people"} svg={"people"} value={"30"} />
                                    <SidePanelItem label={"Performance"} last={true} svg={"preformance"} value={"8.5 / 10"} />
                                </div>

                                <div className={"p-4 flex flex-row gap-4 w-full"}>
                                    <button className={"SidePanelClose mr-4"} type={"button"} onClick={() => handleClick(hallIndex, false)}>
                                        <div className={"p-2 w-24 h-10 flex flex-col justify-center items-center"}>
                                            <span className={"text-red-400 w-6 h-6"}>
                                                <svg viewBox="0 0 68.66 64.48">
                                                    <path
                                                        d="M24.41,31.46,1,0H19.58L35.5,23.23,50.91,0H67.48L43.86,31.46l24.8,33H50.12L33.41,40.33l-16,24.15H0Z"
                                                        fill="currentColor"
                                                    ></path>
                                                </svg>
                                            </span>
                                            <span className={"text-xs mt-1 leading-3"}>back</span>
                                        </div>
                                    </button>
                                    <button className={"bg-red-400 rounded-full text-white px-12 py-2 flex justify-center items-center"} type={"button"}>
                                        {t("More info")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default SidePanel;
